import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { IaScreenerQuestion, IaScreenerSuggestion } from '@app/api';
import { CenteredModal, IconChip, SquareButton } from '@app/core';
import { Button, ThreeDotsSpinner } from 'components';
import {
  buildScreenerFromIaQuestions,
  END_JUMP,
  FieldWithCriterion,
  getDisplayableIaQuestions,
  getIaChoiceState,
  IaChoiceState,
  isIaChoiceQuestion,
} from 'lib/iaScreenerUtils';

const CHOICE_CRITERIA_KEY =
  'screeningForm.create.questionBuilder.choiceList.criteria';

const CHIP_BY_STATE: Record<
  IaChoiceState,
  { color: 'green' | 'yellow' | 'ink' | 'red'; icon?: string }
> = {
  qualify: { color: 'green', icon: 'ri-check-line' },
  tolerate: { color: 'yellow', icon: 'ri-subtract-line' },
  none: { color: 'ink' },
  disqualify: { color: 'red', icon: 'ri-close-line' },
};

const FIELD_KIND_KEYS: Partial<Record<IaScreenerQuestion['type'], string>> = {
  SHORT_TEXT: 'editSession.screening.iaSuggestion.shortText',
  LONG_TEXT: 'editSession.screening.iaSuggestion.longText',
  INFORMATION_PAGE: 'editSession.screening.iaSuggestion.informationPage',
};

interface Props {
  attempts: IaScreenerSuggestion[];
  generating: boolean;
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  onAccept: (items: FieldWithCriterion[]) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  headerIcon: {
    fontSize: theme.iconSize.regular,
    lineHeight: '100%',
    color: theme.colors.accentAccessible,
  },
  questions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  titleLine: {
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'flex-start',
    marginBottom: theme.marginBase,
  },
  number: {
    ...theme.fonts.label,
    ...theme.mutedColorCombinations.accent,
    flex: 'none',
    width: theme.height.chips,
    height: theme.height.chips,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.std,
  },
  title: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
    lineHeight: 1.45,
  },
  required: {
    color: theme.colors.redAccessible,
  },
  details: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    paddingLeft: theme.height.chips + theme.marginBase,
    marginBottom: theme.marginBase,
    whiteSpace: 'pre-line',
  },
  choices: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase,
    paddingLeft: theme.height.chips + theme.marginBase,
  },
  choice: {
    borderRadius: theme.height.chips / 2,
  },
  buttonIcon: {
    fontSize: theme.iconSize.small,
    lineHeight: '100%',
    marginRight: theme.marginBase,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.marginBase * 4,
  },
  // The shared spinner paints its dots white, which is invisible on the light
  // background of this modal and of its buttons.
  spinner: {
    '& .dot-elastic, & .dot-elastic::before, & .dot-elastic::after': {
      backgroundColor: theme.colors.accentAccessible,
    },
  },
  attempts: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  attemptCount: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    minWidth: theme.marginBase * 5,
    textAlign: 'center',
  },
}));

export const ScreeningIaSuggestion = ({
  attempts,
  generating,
  isOpen,
  onClose,
  onRetry,
  onAccept,
}: Props) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const [displayed, setDisplayed] = useState(0);
  const newestId = attempts[attempts.length - 1]?.id;
  useEffect(() => {
    setDisplayed(Math.max(attempts.length - 1, 0));
  }, [newestId, attempts.length]);

  const questions = useMemo(
    () =>
      getDisplayableIaQuestions(attempts[displayed]?.result?.questions || []),
    [attempts, displayed],
  );
  const jumpLabel = useCallback(
    (jump?: string) => {
      if (jump === END_JUMP) {
        return t('editSession.screening.iaSuggestion.jumpEnd');
      }
      const index = questions.findIndex((question) => question.id === jump);

      return index >= 0
        ? t('editSession.screening.iaSuggestion.jumpTo', { number: index + 1 })
        : null;
    },
    [questions, t],
  );
  const loading = generating && attempts.length === 0;

  const accept = useCallback(() => {
    onAccept(buildScreenerFromIaQuestions(questions));
  }, [onAccept, questions]);

  return (
    <CenteredModal
      xlarge
      isOpen={isOpen}
      onRequestClose={onClose}
      trackingName="Screening IA suggestion"
      chip={
        <i className={classnames('ri-sparkling-2-line', classes.headerIcon)} />
      }
      title={t('editSession.screening.iaSuggestion.title')}
      text={
        loading
          ? t('editSession.screening.iaSuggestion.loading')
          : t('editSession.screening.iaSuggestion.subtitle', {
              count: questions.length,
            })
      }
      leftButtons={
        !loading && (
          <div className={classes.attempts}>
            {attempts.length < 3 && (
              <Button
                key="retry"
                className={classes.spinner}
                color="ink"
                variant="muted"
                onClick={onRetry}
                isLoading={generating}
                disabled={generating}
              >
                <i
                  className={classnames('ri-refresh-line', classes.buttonIcon)}
                />
                {t('editSession.screening.iaSuggestion.retry')}
              </Button>
            )}
            {attempts.length > 1 && (
              <>
                <SquareButton
                  icon="ri-arrow-left-s-line"
                  color="ink"
                  disabled={displayed === 0}
                  onClick={() => setDisplayed(displayed - 1)}
                />
                <span className={classes.attemptCount}>
                  {displayed + 1} / {attempts.length}
                </span>
                <SquareButton
                  icon="ri-arrow-right-s-line"
                  color="ink"
                  disabled={displayed >= attempts.length - 1}
                  onClick={() => setDisplayed(displayed + 1)}
                />
              </>
            )}
          </div>
        )
      }
      buttons={
        !loading && (
          <Button
            key="use"
            color="accent"
            onClick={accept}
            disabled={generating}
          >
            <i
              className={classnames(
                'ri-checkbox-circle-line',
                classes.buttonIcon,
              )}
            />
            {t('editSession.screening.iaSuggestion.use')}
          </Button>
        )
      }
    >
      {loading ? (
        <div className={classnames(classes.loading, classes.spinner)}>
          <ThreeDotsSpinner />
        </div>
      ) : (
        <div className={classes.questions}>
          {questions.map((question, index) => {
            const kindKey = FIELD_KIND_KEYS[question.type];
            const defaultJump = jumpLabel(question.jump);
            return (
              <div key={question.id || index} title={question.purpose}>
                <div className={classes.titleLine}>
                  <span className={classes.number}>{index + 1}</span>
                  <span className={classes.title}>
                    {question.title}{' '}
                    {question.type !== 'INFORMATION_PAGE' && (
                      <span className={classes.required}>*</span>
                    )}
                  </span>
                </div>
                {(question.description || kindKey) && (
                  <div className={classes.details}>
                    {[question.description, kindKey && t(kindKey)]
                      .filter(Boolean)
                      .join('\n')}
                  </div>
                )}
                {isIaChoiceQuestion(question) && (
                  <div className={classes.choices}>
                    {(question.choices || []).map((choice, choiceIndex) => {
                      const state = getIaChoiceState(question, choice);
                      const chip = CHIP_BY_STATE[state];
                      const jump = jumpLabel(choice.jump);
                      return (
                        <IconChip
                          key={choiceIndex}
                          className={classes.choice}
                          variant="std"
                          color={chip.color}
                          icon={chip.icon}
                          alt={
                            state !== 'none'
                              ? t(`${CHOICE_CRITERIA_KEY}.${state}`)
                              : undefined
                          }
                          label={
                            jump ? `${choice.label} ${jump}` : choice.label
                          }
                        />
                      );
                    })}
                  </div>
                )}
                {defaultJump && (
                  <div className={classes.details}>
                    {t(
                      isIaChoiceQuestion(question)
                        ? 'editSession.screening.iaSuggestion.defaultJump'
                        : 'editSession.screening.iaSuggestion.nextJump',
                      { jump: defaultJump },
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CenteredModal>
  );
};
